import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Produit } from 'app/models/produit';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import { ProduitsService } from 'app/services/produits/produits.service';
import { AwscontrolService, UploadResponse, DeleteResponse } from 'app/services/aws/awscontrol.service';
import Swal from "sweetalert2";
import { HttpErrorResponse } from "@angular/common/http";
import { Location } from '@angular/common';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith, finalize } from 'rxjs/operators';

@Component({
  selector: 'app-modifier-produit',
  templateUrl: './modifier-produit.component.html',
  styleUrls: ['./modifier-produit.component.scss']
})
export class ModifierProduitComponent implements OnInit {

  produit: Produit = new Produit();
  
  // Form Controls (no validators)
  numCommercialFormControl: FormControl;
  emailCommercialFormControl: FormControl;
  agencySearchControl: FormControl;
  
  // File handling
  selectedFiles: FileList | null = null;
  fileNames: any[] = [];
  allAttch: any[] = [];
  
  // Images
  public image: string = '';
  
  // Data
  public idProduitAgency: any;
  agenciesApp: any[] = [];
  filteredAgencies: Observable<any[]>;
  
  // Selected agency
  NameAgence: string = '';
  selectedAgencyId: string = '';
  
  // Loading states
  isUploading: boolean = false;
  isSubmitting: boolean = false;
  isLoading: boolean = true;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private location: Location,
    private produitService: ProduitsService,
    private agenciesService: AgenciesService,
    private awsService: AwscontrolService
  ) {
    this.numCommercialFormControl = new FormControl('');
    this.emailCommercialFormControl = new FormControl('');
    this.agencySearchControl = new FormControl('');
  }

  ngOnInit(): void {
    this.loadAgencies();
    this.idProduitAgency = this.activatedRoute.snapshot.paramMap.get('id');
    this.loadProduct();
  }

  loadAgencies(): void {
    // Use 'any' to avoid type mismatch (service returns array)
    this.agenciesService.getList().subscribe(
      (data: any) => {
        this.agenciesApp = data;
        // Set up autocomplete after agencies loaded
        this.filteredAgencies = this.agencySearchControl.valueChanges.pipe(
          startWith(''),
          map(value => this._filterAgencies(value))
        );
        // If product already loaded, set agency value
        if (this.produit && this.produit.agencyName) {
          // Cast to string to avoid type mismatch
          this.setAgencyFromName(String(this.produit.agencyName));
        }
      },
      error => console.error('Error loading agencies:', error)
    );
  }

  loadProduct(): void {
    this.isLoading = true;
    this.produitService.getProduit(this.idProduitAgency).subscribe(
      (data: any) => {
        this.produit = data;
        this.image = data.imageUrl || '';
        if (this.image) this.allAttch[0] = this.image;
        
        // Set commercial fields
        if (data.numCommercial) {
          this.numCommercialFormControl.setValue(data.numCommercial);
        }
        if (data.emailCommercial) {
          this.emailCommercialFormControl.setValue(data.emailCommercial);
        }
        
        // Handle categoryList: if string, split into array; if array, keep it
        if (this.produit.categoryList) {
          if (typeof this.produit.categoryList === 'string') {
            this.produit.categoryList = (this.produit.categoryList as string).split(',').filter(c => c.trim());
          } else if (!Array.isArray(this.produit.categoryList)) {
            this.produit.categoryList = [];
          }
        } else {
          this.produit.categoryList = [];
        }
        
        // Set agency name and search control – using data directly and casting
        if (data.agencyName) {
          const agencyName = String(data.agencyName); // force string
          this.NameAgence = agencyName;
          // Wait for agencies to be loaded then set the search field
          if (this.agenciesApp && this.agenciesApp.length > 0) {
            this.setAgencyFromName(agencyName);
          }
        }
        
        this.isLoading = false;
      },
      error => {
        console.error('Error loading product:', error);
        this.isLoading = false;
        Swal.fire({
          icon: "error",
          title: "Erreur",
          text: "Erreur lors du chargement du produit",
        });
      }
    );
  }

  private setAgencyFromName(name: string): void {
    const found = this.agenciesApp.find(a => a.name === name);
    if (found) {
      this.selectedAgencyId = found.id;
      this.agencySearchControl.setValue(found.name);
    } else {
      this.agencySearchControl.setValue(name);
    }
  }

  private _filterAgencies(value: string): any[] {
    const filterValue = value ? value.toLowerCase() : '';
    return this.agenciesApp ? this.agenciesApp.filter(agency => 
      agency.name.toLowerCase().includes(filterValue)
    ) : [];
  }

  displayAgency(agency: any): string {
    return agency ? agency.name : '';
  }

  onAgencySelected(event: any): void {
    const selected = event.option.value;
    if (selected) {
      this.NameAgence = selected.name;
      this.selectedAgencyId = selected.id;
    }
  }

  // ========== IMAGE UPLOAD ==========
  selectFileimg(e: any): void {
    this.selectedFiles = e.target.files;
    if (this.selectedFiles && this.selectedFiles.length > 0) {
      this.uploadMainImage(this.selectedFiles[0]);
    }
  }

  uploadMainImage(file: File): void {
    if (!file) return;
    this.isUploading = true;
    const folder = 'produits';

    this.awsService.uploadFile(file, folder)
      .pipe(finalize(() => this.isUploading = false))
      .subscribe(
        (response: UploadResponse) => {
          if (response.success && response.urls && response.urls.length > 0) {
            const url = response.urls[0];
            this.image = url;
            this.allAttch[0] = url;
            localStorage.setItem('prodImg', JSON.stringify(url));
            Swal.fire({
              icon: "success",
              title: "Image téléchargée",
              text: "Image principale mise à jour",
              timer: 1500
            });
          } else {
            Swal.fire({
              icon: "error",
              title: "Erreur",
              text: response.error || "Échec du téléchargement",
            });
          }
        },
        (error) => {
          console.error('Upload error:', error);
          Swal.fire({
            icon: "error",
            title: "Erreur",
            text: "Erreur lors du téléchargement de l'image",
          });
        }
      );
  }

  deleteMainImage(): void {
    if (!this.image) return;

    Swal.fire({
      title: 'Êtes-vous sûr?',
      text: "Cette action est irréversible!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Oui, supprimer!',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.awsService.deleteFile(this.image).subscribe(
          (response: DeleteResponse) => {
            if (response.status === 'success') {
              this.image = '';
              this.allAttch[0] = '';
              Swal.fire({
                icon: 'success',
                title: 'Supprimé!',
                text: "L'image a été supprimée.",
                timer: 1500
              });
            } else {
              Swal.fire({
                icon: 'error',
                title: 'Erreur',
                text: response.error || "Échec de la suppression",
              });
            }
          },
          (error) => {
            console.error('Delete error:', error);
            Swal.fire({
              icon: 'error',
              title: 'Erreur',
              text: "Erreur lors de la suppression",
            });
          }
        );
      }
    });
  }

  // ========== SUBMIT ==========
  updateProduit(): void {
    if (this.isUploading) {
      Swal.fire({
        icon: "warning",
        title: "Téléchargement en cours",
        text: "Veuillez attendre la fin du téléchargement de l'image",
      });
      return;
    }

    this.isSubmitting = true;

    // Convert categoryList array to comma-separated string
    let categoryString = '';
    if (this.produit.categoryList && Array.isArray(this.produit.categoryList)) {
      categoryString = this.produit.categoryList.filter(c => c.trim()).join(',');
    } else if (typeof this.produit.categoryList === 'string') {
      categoryString = this.produit.categoryList;
    }

    // Build payload
    const payload: any = {
      id: this.produit.id,
      name: this.produit.name || '',
      description: this.produit.description || '',
      prix: this.produit.prix || '',
      agencyName: this.NameAgence || '',
      categoryList: categoryString,
      imageUrl: this.image || this.produit.imageUrl || '',
      videoUrlProduit: this.produit.videoUrlProduit || '',
      nomCommercial: this.produit.nomCommercial || '',
      numCommercial: this.numCommercialFormControl.value || '',
      emailCommercial: this.emailCommercialFormControl.value || ''
    };

    console.log('📦 Updating with payload:', payload);

    this.produitService.updateProduit(this.idProduitAgency, payload).subscribe(
      () => {
        this.isSubmitting = false;
        Swal.fire({
          icon: "success",
          title: "Succès",
          text: "Produit modifié avec succès",
          timer: 2000
        });
        setTimeout(() => {
          if (this.selectedAgencyId) {
            this.router.navigate(['/list-produits', this.selectedAgencyId]);
          } else {
            this.navigateBack();
          }
        }, 500);
      },
      (error: HttpErrorResponse) => {
        this.isSubmitting = false;
        console.error("Update error:", error);
        let errorMsg = "Une erreur s'est produite lors de la modification du produit";
        if (error.error?.message) errorMsg = error.error.message;
        Swal.fire({ icon: "error", title: "Erreur", text: errorMsg });
      }
    );
  }

  navigateBack(): void {
    this.location.back();
  }
}